import React from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import MuiTextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api'
import BaseDisplayItem, {
    BaseDisplayItemProps,
    DisplayItemBase,
    DisplayMode,
} from '@/feature/DisplayItems/BaseDisplayItem'

/* ---------- Types ---------- */

type GeoValue = {
    latitude: number | string | null
    longitude: number | string | null
    zoom?: number
} | null

interface Kedo {
    t: (key: string) => string
}

interface Props extends BaseDisplayItemProps<GeoValue, DisplayItemBase> {
    kedo: Kedo
    loadError?: boolean
    /** Prefer passing via env/config, not hardcoding */
    googleMapsApiKey: string
    mode: DisplayMode
}

interface State {
    value: GeoValue
    latitude: number | string | null
    longitude: number | string | null
    zoom: number
}

/* ---------- Component ---------- */

class GeolocationField extends BaseDisplayItem<
    GeoValue,
    DisplayItemBase,
    HTMLInputElement
> {
    constructor(props: Props) {
        super(props)

        const lat = props.value?.latitude ?? null
        const lng = props.value?.longitude ?? null
        const zoom =
            typeof props.value?.zoom === 'number' ? props.value!.zoom! : 10

        this.state = {
            value: props.value ?? null,
            latitude: lat,
            longitude: lng,
            zoom,
        }

        this.onMarkerDragEnd = this.onMarkerDragEnd.bind(this)
        this.onChangeLatitude = this.onChangeLatitude.bind(this)
        this.onChangeLongitude = this.onChangeLongitude.bind(this)
    }

    /* ---------- Helpers ---------- */

    getValue(): GeoValue {
        return {
            latitude: this.state.latitude,
            longitude: this.state.longitude,
            zoom: this.state.zoom,
        }
    }

    private parseNumberOrNull(v: string): number | null {
        const trimmed = v.trim()
        if (!trimmed) return null
        const n = Number(trimmed)
        return Number.isFinite(n) ? n : null
    }

    private pushChange(
        latitude: number | string | null,
        longitude: number | string | null
    ) {
        const props = this.props as Props
        props.changeValue?.(
            props.item.id,
            { latitude, longitude, zoom: this.state.zoom },
            props.item
        )
    }

    /* ---------- Events ---------- */

    onMarkerDragEnd(e: google.maps.MapMouseEvent) {
        if (!e?.latLng) return

        const lng = e.latLng.lng()
        const lat = e.latLng.lat()

        this.setState({ latitude: lat, longitude: lng }, () =>
            this.pushChange(lat, lng)
        )
    }

    onChangeLatitude(e: React.ChangeEvent<HTMLInputElement>) {
        const raw = e.target.value
        // keep typing-friendly: store raw string while user edits
        this.setState({ latitude: raw }, () =>
            this.pushChange(this.state.latitude, this.state.longitude)
        )
    }

    onChangeLongitude(e: React.ChangeEvent<HTMLInputElement>) {
        const raw = e.target.value
        this.setState({ longitude: raw }, () =>
            this.pushChange(this.state.latitude, this.state.longitude)
        )
    }

    /* ---------- Lifecycle ---------- */

    componentDidUpdate(prevProps: Props) {
        const next = (this.props as Props).value
        // Sync if parent value changes
        if (prevProps.value !== next) {
            this.setState({
                value: next ?? null,
                latitude: next?.latitude ?? null,
                longitude: next?.longitude ?? null,
                zoom: typeof next?.zoom === 'number' ? next.zoom : 10,
            })
            return
        }

        // Same as BaseDisplayItem: leaving edit without save — props are canonical
        const leftEditLike =
            prevProps.mode === 'edit' || prevProps.mode === 'show/edit'
        if (leftEditLike && this.props.mode === 'show') {
            this.setState({
                value: next ?? null,
                latitude: next?.latitude ?? null,
                longitude: next?.longitude ?? null,
                zoom: typeof next?.zoom === 'number' ? next.zoom : 10,
            })
        }
    }

    /* ---------- Render pieces ---------- */

    private renderMap(): React.ReactNode {
        const props = this.props as Props

        const containerStyle: React.CSSProperties = {
            width: '800px',
            height: '600px',
        }

        const defLoc = { lng: 4.895168, lat: 52.370216 } // Amsterdam

        const lngN =
            typeof this.state.longitude === 'number'
                ? this.state.longitude
                : this.state.longitude
                ? this.parseNumberOrNull(String(this.state.longitude))
                : null

        const latN =
            typeof this.state.latitude === 'number'
                ? this.state.latitude
                : this.state.latitude
                ? this.parseNumberOrNull(String(this.state.latitude))
                : null

        const curLoc =
            lngN !== null && latN !== null ? { lng: lngN, lat: latN } : null

        // Legacy behavior: if no longitude and not editing -> empty
        if (
            !this.state.longitude &&
            props.mode !== 'edit' &&
            props.mode !== 'show/edit'
        ) {
            return <div />
        }

        return (
            <LoadScript googleMapsApiKey={props.googleMapsApiKey}>
                <GoogleMap
                    center={curLoc ?? defLoc}
                    mapContainerStyle={containerStyle}
                    zoom={this.state.zoom}
                >
                    <Marker
                        draggable={
                            props.mode === 'edit' || props.mode === 'show/edit'
                        }
                        onDragEnd={this.onMarkerDragEnd}
                        position={curLoc ?? defLoc}
                    />
                </GoogleMap>
            </LoadScript>
        )
    }

    private renderCoordsEditor(): React.ReactNode {
        return (
            <Stack spacing={2} sx={{ maxWidth: 420, mt: 1 }}>
                <Typography variant="h6">Geolocation</Typography>

                <MuiTextField
                    size="small"
                    label="Latitude"
                    inputRef={this.innerRef}
                    value={this.state.latitude ?? ''}
                    onChange={this.onChangeLatitude}
                    error={this.isInvalid()}
                    helperText={this.isInvalid() ? this.getErrorText() : ''}
                    fullWidth
                />

                <MuiTextField
                    size="small"
                    label="Longitude"
                    value={this.state.longitude ?? ''}
                    onChange={this.onChangeLongitude}
                    error={this.isInvalid()}
                    helperText={this.isInvalid() ? this.getErrorText() : ''}
                    fullWidth
                />
            </Stack>
        )
    }

    /* ---------- BaseDisplayItem implementations ---------- */

    renderShow(): React.ReactNode {
        return <Box sx={{ position: 'relative' }}>{this.renderMap()}</Box>
    }

    renderEdit(): React.ReactNode {
        // Your legacy component always showed the map in edit mode (and even in show/edit)
        // It also had an unused coordinate editor. Here we show both (map + inputs).
        // If you want ONLY the map (matching exact behavior), remove renderCoordsEditor().
        return (
            <Box sx={{ position: 'relative' }}>
                {this.renderMap()}
                {this.renderCoordsEditor()}
            </Box>
        )
    }

    renderSearch(): React.ReactNode {
        return null
    }

    /* ---------- Keep legacy render behavior for loadError ---------- */

    render(): React.ReactNode {
        const props = this.props as Props
        if (props.loadError) {
            return <div>{props.kedo.t('General error')}</div>
        }
        return super.render()
    }
}

export default GeolocationField
